/** Number of side-menu groups (`number` 0..12 on the navigation items). */
export const MENU_COUNT = 13;

/**
 * localStorage key prefix for the open/closed state of the side-menu groups.
 *
 * The profile setting (`user_settings_menu`) is only the start-up default;
 * a group the user opened or closed by hand used to fall back to that default
 * on every reload and app update (HIP-89). The key is suffixed with the user
 * id so a shared browser keeps one state per account.
 */
export const OPENED_MENUS_KEY = 'openedMenus';

function storageKey(userId?: string): string {
    return userId ? `${OPENED_MENUS_KEY}:${userId}` : OPENED_MENUS_KEY;
}

/**
 * Remembers which groups are open. Never throws — localStorage can be
 * unavailable (private mode, blocked storage).
 */
export function storeOpenedMenus(
    openedMenus: boolean[],
    userId?: string
): void {
    const opened = openedMenus.flatMap((open, number) =>
        open ? [number] : []
    );
    try {
        localStorage.setItem(storageKey(userId), JSON.stringify(opened));
    } catch {}
}

/**
 * The remembered open/closed state as a `MENU_COUNT`-sized boolean array, or
 * `null` when nothing (valid) was stored for this user yet.
 */
export function readOpenedMenus(userId?: string): boolean[] | null {
    let stored: string | null = null;
    try {
        stored = localStorage.getItem(storageKey(userId));
    } catch {
        return null;
    }
    if (!stored) {
        return null;
    }
    let opened: unknown;
    try {
        opened = JSON.parse(stored);
    } catch {
        return null;
    }
    if (!Array.isArray(opened)) {
        return null;
    }
    const openedMenus: boolean[] = Array(MENU_COUNT).fill(false);
    for (const number of opened) {
        if (Number.isInteger(number) && number >= 0 && number < MENU_COUNT) {
            openedMenus[number] = true;
        }
    }
    return openedMenus;
}

/** Forgets the remembered state, so a new login starts with the profile defaults. */
export function clearOpenedMenus(userId?: string): void {
    try {
        localStorage.removeItem(storageKey(userId));
    } catch {}
}
